<template>
  <span class="rs-tooltip" style="display:none" />
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import $ from 'jquery';
import Tooltip from 'bootstrap/js/dist/tooltip';
import type { TTooltipPosition } from '@/shared/types';

interface ITooltipNonReactive {
  _tooltipInstance: Tooltip | undefined;
  _targetEl: HTMLElement | undefined;
  _onShown: (() => void) | undefined;
}

export default defineComponent({
  name: 'RsTooltip',
  props: {
    target: {
      type: String,
      default: null,
      required: true,
    },
    variant: String,
    triggers: String,
    title: String,
    position: {
      // Bootstrap 4's _getAttachment() only maps top|bottom|left|right|auto — top-start/top-end etc. are not surfaced.
      type: String as PropType<TTooltipPosition>,
      default: 'top',
    },
    boundary: String,
    delay: {
      type: [Number, Object],
      default() {
        return { delay: 50 };
      },
    },
  },
  watch: {
    title(): void {
      this.cleanupTooltip();
      this.initTooltip();
    },
  },
  mounted() {
    this.initTooltip();
  },
  beforeUnmount() {
    this.cleanupTooltip();
  },
  methods: {
    initTooltip(): void {
      const self = this as typeof this & ITooltipNonReactive;
      const targetEl = this.target ? document.getElementById(this.target) : null;
      if (!targetEl || !this.title) return;
      const delayVal = typeof this.delay === 'number' ? this.delay : (this.delay as { show?: number })?.show ?? 50;
      self._tooltipInstance = new Tooltip(targetEl, {
        title: this.title,
        placement: this.position,
        trigger: this.triggers || 'hover focus',
        delay: delayVal,
        container: document.body,
        boundary: 'window',
        popperConfig: { positionFixed: true },
      });
      // trigger: 'click' toggle() looks this up via jQuery's data cache, normally seeded
      // by $(el).tooltip(); without it, every click spawns a duplicate instance/listener.
      $(targetEl).data('bs.tooltip', self._tooltipInstance);
      self._targetEl = targetEl;
      self._onShown = () => {
        const tooltipId = targetEl.getAttribute('aria-describedby');
        if (!tooltipId) return;
        const ttEl = document.getElementById(tooltipId);
        if (!ttEl) return;
        ttEl.classList.add('b-tooltip');
        if (this.variant) {
          this.variant.split(' ').forEach((cls: string) => ttEl.classList.add(`b-tooltip-${cls}`));
        }
      };
      targetEl.addEventListener('shown.bs.tooltip', self._onShown);
    },
    cleanupTooltip(): void {
      const self = this as typeof this & ITooltipNonReactive;
      if (self._targetEl && self._onShown) {
        self._targetEl.removeEventListener('shown.bs.tooltip', self._onShown);
      }
      // Bootstrap's dispose() removes the tip via jQuery which can fail mid-animation; use aria-describedby to remove it directly from the DOM.
      if (self._targetEl) {
        const tooltipId = self._targetEl.getAttribute('aria-describedby');
        if (tooltipId) {
          document.getElementById(tooltipId)?.remove();
        }
      }
      self._tooltipInstance?.dispose();
      self._tooltipInstance = undefined;
      self._targetEl = undefined;
      self._onShown = undefined;
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
.b-tooltip{
  top: 0;
  opacity: 1 !important;
  &-secondary{
    .tooltip-inner{
      padding: 0.5rem 1rem;
      max-width: 400px;
      text-align: left;
      background-color: $secondary-color-dark !important;
    }
    .arrow:before{
      border-bottom-color: $secondary-color-dark !important;
    }
  }
  &-inline {
    .tooltip-inner {
      max-width: unset;
    }
  }
}
</style>
